import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Islak stok mutabakatı (Prompt 12 §5) ve gün kapanışı (SMM fişi).
 *
 *   defter stoğu = açılış ölçümü + teslim alınan − ENDEKS satışı + test amaçlı çekilip tanka geri dökülen
 *   fark         = kapanış ölçümü − defter stoğu
 *
 * Satış İKİ yoldan hesaplanır: endeks farkı (fiziksel gerçek; stok farkı bununla bulunur) ve işlem toplamı
 * (otomasyon kayıtları). İkisi arasındaki fark stok farkı DEĞİL, ayrı bir veri bütünlüğü kontrolüdür.
 */
export const RECONCILIATION_PERIOD_KINDS = ["DAY", "SHIFT"];
export const reconciliationPeriodKindSchema = z.enum(RECONCILIATION_PERIOD_KINDS);
export const RECONCILIATION_PERIOD_KIND_LABELS = {
    DAY: "Gün",
    SHIFT: "Vardiya",
};
export const RECONCILIATION_CLASSIFICATIONS = ["NORMAL", "WATCH", "SUSPICIOUS", "CRITICAL"];
export const reconciliationClassificationSchema = z.enum(RECONCILIATION_CLASSIFICATIONS);
export const RECONCILIATION_CLASSIFICATION_LABELS = {
    NORMAL: "Normal",
    WATCH: "İzlemede",
    SUSPICIOUS: "Şüpheli",
    CRITICAL: "Kritik",
};
export const RECONCILIATION_RULE_CODES = [
    "DAILY_VARIANCE",
    "CUMULATIVE_TREND",
    "IDLE_LEVEL_DROP",
    "WATER_RISE",
    "SALES_CHECK",
];
export const reconciliationRuleSchema = z.enum(RECONCILIATION_RULE_CODES);
export const RECONCILIATION_RULE_LABELS = {
    DAILY_VARIANCE: "Tek dönem farkı",
    CUMULATIVE_TREND: "Kümülatif negatif eğilim",
    IDLE_LEVEL_DROP: "Satışsız seviye düşüşü",
    WATER_RISE: "Su seviyesi artışı",
    SALES_CHECK: "Endeks ↔ işlem farkı",
};
/** Hesabın güvenilirliğini etkileyen gözlemler — kural bulgusu DEĞİLDİR, veri kalitesi uyarısıdır. */
export const RECONCILIATION_DATA_QUALITY_CODES = [
    "OPENING_READING_LATE",
    "CLOSING_READING_EARLY",
    "TOTALIZER_MISSING",
    "TOTALIZER_ANOMALY",
    "DELIVERY_UNCONFIRMED",
    "DELIVERY_DISPUTED",
    "NO_READINGS",
];
export const reconciliationDataQualitySchema = z.enum(RECONCILIATION_DATA_QUALITY_CODES);
export const RECONCILIATION_DATA_QUALITY_LABELS = {
    OPENING_READING_LATE: "Açılış okuması dönem başından geç alınmış",
    CLOSING_READING_EARLY: "Kapanış okuması dönem sonundan erken alınmış",
    TOTALIZER_MISSING: "Endeks okuması yok; satış işlem toplamından alındı",
    TOTALIZER_ANOMALY: "Endeks serisinde anomali var; o adım satışa katılmadı",
    DELIVERY_UNCONFIRMED: "Dönemde henüz onaylanmamış (ölçülmüş) dolum var",
    DELIVERY_DISPUTED: "Dönemde itiraz edilmiş dolum var",
    NO_READINGS: "Dönemde tank okuması yok",
};
export const RECONCILIATION_STATUSES = ["DRAFT", "APPROVED"];
export const reconciliationStatusSchema = z.enum(RECONCILIATION_STATUSES);
export const RECONCILIATION_STATUS_LABELS = {
    DRAFT: "Taslak",
    APPROVED: "Onaylandı",
};
export const FUEL_LEDGER_STATUSES = ["NONE", "POSTED", "QUEUED"];
export const fuelLedgerStatusSchema = z.enum(FUEL_LEDGER_STATUSES);
export const FUEL_LEDGER_STATUS_LABELS = {
    NONE: "Fiş gerekmedi",
    POSTED: "Fiş kesildi",
    QUEUED: "Hesap eşleştirmesi bekliyor",
};
export const reconciliationFindingSchema = z.object({
    rule: reconciliationRuleSchema,
    severity: z.enum(["WATCH", "SUSPICIOUS", "CRITICAL"]),
    measured: z.string(),
    threshold: z.string(),
});
export const reconciliationDataQualityEntrySchema = z.object({
    code: reconciliationDataQualitySchema,
    detail: z.string().nullable(),
});
export const fuelReconciliationSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    tankId: uuidSchema,
    tankNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    periodKind: reconciliationPeriodKindSchema,
    businessDate: localDateSchema,
    shiftId: uuidSchema.nullable(),
    periodStart: timestampSchema,
    periodEnd: timestampSchema,
    /** Bu aşamada hep `GROSS` (ortam hacmi); 15 °C tabanlı mutabakat ileride ayar olacak. */
    volumeBasis: z.literal("GROSS"),
    openingL: quantitySchema,
    closingL: quantitySchema,
    deliveredL: quantitySchema,
    totalizerSalesL: quantitySchema,
    transactionSalesL: quantitySchema,
    testReturnedL: quantitySchema,
    bookStockL: quantitySchema,
    varianceL: quantitySchema.describe("kapanış − defter stoğu; negatif = kayıp"),
    variancePercent: rateSchema.nullable(),
    salesCheckDifferenceL: quantitySchema,
    cumulativeVarianceL: quantitySchema,
    cumulativeSalesL: quantitySchema,
    idleDropLPerHour: quantitySchema.nullable(),
    waterRiseMm: z.string().nullable(),
    classification: reconciliationClassificationSchema,
    findings: z.array(reconciliationFindingSchema),
    dataQuality: z.array(reconciliationDataQualityEntrySchema),
    status: reconciliationStatusSchema,
    explanation: z.string().nullable(),
    normalLossL: quantitySchema.nullable(),
    abnormalLossL: quantitySchema.nullable(),
    surplusL: quantitySchema.nullable(),
    lossUnitCost: z.string().nullable(),
    lossAmount: moneySchema.nullable(),
    journalEntryId: uuidSchema.nullable(),
    ledgerStatus: fuelLedgerStatusSchema,
    approvedAt: timestampSchema.nullable(),
    approvedBy: uuidSchema.nullable(),
    version: z.number().int(),
});
export const listFuelReconciliationsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    tankId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    classification: reconciliationClassificationSchema.optional(),
    status: reconciliationStatusSchema.optional(),
});
export const fuelReconciliationPageSchema = pageOf(fuelReconciliationSchema);
export const computeReconciliationSchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
});
export const computeReconciliationResultSchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
    items: z.array(fuelReconciliationSchema),
    /** Hesaplanamayan tanklar (kalibrasyon / okuma eksik) — hata değil, bilgi. */
    skipped: z.array(z.object({ tankId: uuidSchema, reason: z.string() })),
});
export const approveReconciliationSchema = z.object({
    version: z.number().int().min(1),
    explanation: z.string().trim().max(1000).optional().describe("Fark mutlak alt sınırı aşıyorsa ZORUNLU"),
});
/** Kümülatif fark grafiği (web'de ECharts). */
export const cumulativeVarianceQuerySchema = z.object({
    tankId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
});
export const cumulativeVariancePointSchema = z.object({
    businessDate: localDateSchema,
    varianceL: quantitySchema,
    cumulativeVarianceL: quantitySchema,
    salesL: quantitySchema,
    cumulativeSalesL: quantitySchema,
    cumulativePercent: rateSchema.nullable(),
    classification: reconciliationClassificationSchema,
});
export const cumulativeVarianceSchema = z.object({
    tankId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    points: z.array(cumulativeVariancePointSchema),
});
// ── Gün kapanışı (SMM) ───────────────────────────────────────────────────────
export const fuelDailyClosingSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    businessDate: localDateSchema,
    soldL: quantitySchema.describe("Test HARİÇ satılan litre"),
    salesAmount: moneySchema,
    unitCost: z.string(),
    cogsAmount: moneySchema,
    cogsEntryId: uuidSchema.nullable(),
    cogsLedgerStatus: fuelLedgerStatusSchema,
    salesEntryId: uuidSchema.nullable(),
    salesLedgerStatus: fuelLedgerStatusSchema,
    closedAt: timestampSchema,
    closedBy: uuidSchema.nullable(),
});
export const closeFuelDaySchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
});
export const closeFuelDayResultSchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
    items: z.array(fuelDailyClosingSchema),
    warnings: z.array(z.string()),
});
export const listFuelDailyClosingsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    productId: uuidSchema.optional(),
});
export const fuelDailyClosingPageSchema = pageOf(fuelDailyClosingSchema);
export const reconciliationParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=reconciliation.js.map